'use client'

import { useEffect, useRef } from 'react'
import { getTracerProvider } from './browser-tracer-provider-registry'
import {
  initBrowserLogging,
  interceptConsole,
  restoreConsole,
  setupErrorHandlers,
  getLoggerProvider,
  shutdownLogging,
} from './otel-browser-logger'
import { requiresCookieConsent } from '../feature-flags'
import { useCookieConsentStore } from '@/entities/cookie-consent/model/use-cookie-consent-store'

interface SigNozProviderProps {
  children: React.ReactNode
}

/**
 * SigNoz Browser Telemetry Provider
 *
 * Initializes OpenTelemetry browser tracing and logging.
 * Replaces HyperDXProvider with standard OTEL instrumentation.
 *
 * Features:
 * - Distributed tracing with trace propagation to backend
 * - Console log capture (log/warn/error)
 * - Unhandled error and promise rejection capture
 * - Auto-instrumentation of fetch/XHR requests
 * - Document load timing
 * - Page unload flush to prevent data loss
 * - 10% sampling to reduce overhead
 *
 * Environment Variables (auto-injected via next.config.js):
 * - NEXT_PUBLIC_TRACING_ENABLED: Enable/disable tracing
 * - NEXT_PUBLIC_SIGNOZ_INGESTION_KEY: SigNoz ingestion key
 * - NEXT_PUBLIC_OTEL_INGEST_URL: SigNoz OTLP endpoint
 * - NEXT_PUBLIC_OTEL_SERVICE_NAME: Frontend service name
 * - NEXT_PUBLIC_CLOUD_REGION: Region identifier (au/eu)
 */
export function SigNozProvider({ children }: SigNozProviderProps) {
  // Use ref to track cleanup function for error handlers
  const cleanupErrorHandlersRef = useRef<(() => void) | undefined>(undefined)

  // Get consent state - only use if consent is required
  // Use store directly instead of useCookieConsent hook to avoid requiring QueryClient
  // HyperDXProvider renders outside QueryProvider, so we can't use RPC-dependent hooks
  const needsConsent = requiresCookieConsent()
  const { hasConsented, categories } = useCookieConsentStore()

  // Determine if we have performance consent
  // If region doesn't require consent, treat as having consent
  const hasPerformanceConsent = !needsConsent || (hasConsented && categories.performance)

  useEffect(() => {
    // Only initialize in browser environment
    if (typeof window === 'undefined') return

    // Check if tracing is enabled
    const tracingEnabled = process.env.NEXT_PUBLIC_TRACING_ENABLED === 'true'
    const signozIngestionKey = process.env.NEXT_PUBLIC_SIGNOZ_INGESTION_KEY
    const signozIngestUrl = process.env.NEXT_PUBLIC_OTEL_INGEST_URL

    if (!tracingEnabled || !signozIngestionKey || !signozIngestUrl || !hasPerformanceConsent) {
      // console.log('⏭️ SigNoz browser telemetry disabled (missing configuration)')
      return
    }

    // TEST-ONLY sampling override (spec W0 Tier 2). The default root sampler
    // is a 10% ratio, so a Playwright assertion about one specific trace would
    // pass or fail on a coin flip. `?otelSample=1` pins the run to always-on
    // and `?otelSample=0` to never — which is how the `sampledOutIsSilent` row
    // is driven from a real browser. Reading it from the URL rather than an env
    // var keeps the two rows in ONE server process. It only ever narrows to
    // values the sampler already supports and cannot enable tracing that the
    // env + consent checks above have not already allowed.
    const sampleParam = new URLSearchParams(window.location.search).get('otelSample')
    const sampleRatio =
      sampleParam === '1' ? 1 : sampleParam === '0' ? 0 : undefined

    // Get service name (with fallback)
    const serviceName = process.env.NEXT_PUBLIC_OTEL_SERVICE_NAME || 'cova-platform-web'

    // Trace propagation targets - add traceparent headers to these requests
    const tracePropagationTargets = [
      /^https?:\/\/localhost:3000\/api\/.*/, // Local development
      /^https?:\/\/[^/]+\/api\/.*/, // All API routes (any domain)
    ]

    // Tracing loads on this consented path ONLY. `otel-browser-config`
    // imports `@opentelemetry/context-zone`, and zone.js patches the global
    // `Promise`, `IntersectionObserver` and `ResizeObserver` the moment it
    // loads; a static import here put it on every page, since the root layout
    // renders this provider. `cancelled` stops a late load from initialising
    // after consent was revoked; `shutdownTracing` is a no-op if it never did.
    // Init now waits on a chunk fetch: queries sent before it lands go out
    // unlinked, and a ManagedRuntime built in that window keeps the inert
    // tracer until reload, the accepted cost of keeping zone.js off the page.
    // Pinned by `__tests__/zone-free-imports.test.ts`.
    let cancelled = false
    const tracing = import('./otel-browser-config')
    void tracing
      .then(({ initBrowserTracing }) => {
        if (cancelled) return
        initBrowserTracing({
          serviceName,
          signozIngestUrl,
          signozIngestionKey,
          tracePropagationTargets,
          sampleRatio,
        })
      })
      .catch((error: unknown) => console.error('[OTL-001] Failed to initialize', error))

    try {
      // Initialize logging
      initBrowserLogging({
        serviceName,
        signozIngestUrl,
        signozIngestionKey,
      })

      // Intercept console methods
      interceptConsole()

      // Set up global error handlers and store cleanup function
      cleanupErrorHandlersRef.current = setupErrorHandlers()

    } catch (error) {
      console.error('[OTL-001] Failed to initialize', error)
    }

    // Flush telemetry on page hide to prevent data loss
    // Using visibilitychange is more reliable than beforeunload/unload
    // and works with bfcache (back-forward cache)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Flush both tracer and logger providers
        // These are async but we don't await - browser will allow short tasks to complete
        getTracerProvider()?.forceFlush()
        getLoggerProvider()?.forceFlush()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Cleanup function - called when consent is revoked or component unmounts
    // This ensures GDPR compliance by stopping all telemetry collection and export
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      cleanupErrorHandlersRef.current?.()

      // Stop capturing new logs by restoring original console methods
      restoreConsole()

      // Shutdown providers to stop telemetry export (fire-and-forget since cleanup can't be async)
      // Any pending data collected with consent will be flushed during shutdown
      cancelled = true
      // A failed load was already logged by the init chain above; skip it here.
      void tracing
        .then(({ shutdownTracing }) => shutdownTracing(), () => undefined)
        .catch((error: unknown) => console.error('[OTL-001] Failed to shut down', error))
      shutdownLogging()
    }
  }, [hasPerformanceConsent])

  return <>{children}</>
}
